import sunUrl from "@assets/img/sun.png";
import moonUrl from "@assets/img/moon.png";
import { pxChangeVw } from "@/utils";
import useDayjs from "@/hooks/useDayjs";
import { useEffect } from "react";

// 判断值是否为空类型
function isEmptyType(value: any): boolean {
  return value === undefined || value === null || value === "";
}

// 将时间字符串(HH:mm:ss)转换为分钟数
function hoursToMinutes(time: any) {
  if (isEmptyType(time)) return 0;
  const array = time.split(":").map((item: string | number) => +item);
  const hoursToMinutes = array[0] * 60;
  const minutes = array[1] || 0;
  const secondsToMinutes = array[2] ? array[2] / 60 : 0;
  return hoursToMinutes + minutes + secondsToMinutes;
}

// 二阶贝塞尔曲线函数
function getSecondPathPoint(sp: { x: any; y: any }, cp: { x: any; y: any }, ep: { x: any; y: any }) {
  const { x: x0, y: y0 } = sp;
  const { x: x1, y: y1 } = cp;
  const { x: x2, y: y2 } = ep;
  return function (t: number) {
    /* if (t < 0 || t > 1) {
      throw new RangeError('The value of parameter "t" is [0,1]');
    } */
    return {
      x: Math.pow(1 - t, 2) * x0 + 2 * t * (1 - t) * x1 + Math.pow(t, 2) * x2,
      y: Math.pow(1 - t, 2) * y0 + 2 * t * (1 - t) * y1 + Math.pow(t, 2) * y2,
    };
  };
}

// 三阶贝塞尔曲线函数
function getThirdPathPoint(sp: any, cp1: any, cp2: any, ep: any) {
  const { x: x0, y: y0 } = sp;
  const { x: x1, y: y1 } = cp1;
  const { x: x2, y: y2 } = cp2;
  const { x: x3, y: y3 } = ep;
  return function (t: number) {
    /* if (t > 1 || t < 0) {
      throw new Error('The value range of paramter "t" is [0, 1].');
    } */
    return {
      x:
        x0 * Math.pow(1 - t, 3) +
        3 * x1 * t * Math.pow(1 - t, 2) +
        3 * x2 * Math.pow(t, 2) * (1 - t) +
        x3 * Math.pow(t, 3),
      y:
        y0 * Math.pow(1 - t, 3) +
        3 * y1 * t * Math.pow(1 - t, 2) +
        3 * y2 * Math.pow(t, 2) * (1 - t) +
        y3 * Math.pow(t, 3),
    };
  };
}

// 获取小时数组(00, 02, 04...22)
const getHoursArray = () => {
  const evenNumbers = [];
  for (let i = 0; i <= 22; i += 2) {
    evenNumbers.push((i + "").padStart(2, "0"));
  }
  return evenNumbers;
};

// 基础版本贝塞尔曲线图表组件
const BesselChart: React.FC<any> = ({ SunMoon }: any) => {
  const { dayjs } = useDayjs();

  useEffect(() => {
    const sunRiseTime = SunMoon?.Sun?.Rise; // 日出时间
    const nowTime = dayjs().format("HH:mm:ss"); // 当前时间
    const nowDateTime = dayjs().format("YYYY-MM-DD HH:mm:ss");
    const sunSetTime = SunMoon?.Sun?.Set; // 日落时间
    const sunRiseDateTime = SunMoon?.Sun?.EpochRise; // 日出日期时间
    const sunSetDateTime = SunMoon?.Sun?.EpochSet; // 日落日期时间

    const moonRiseTime = SunMoon?.Moon?.Rise; // 月出时间
    // const moonSetTime = SunMoon?.Moon?.Set; // 月落时间

    const moonRiseDateTime = SunMoon?.Moon?.EpochRise; // 月出日期时间(一般当天晚上)
    const moonSetDateTime = SunMoon?.Moon?.EpochSet; // 月落日期时间(一般第二天上午)

    const timeDataOfHours = getHoursArray();
    const num2 = -35; // 曲线凹凸程度 最大凸起 -75 最小凸起(扁平) 75
    const radius = 2; // 标记点半径
    const num3 = 25 + ((375 - 25) / 12) * ((hoursToMinutes(sunSetTime) * 0.5) / 60); // 日落时间点
    const num4 = 25 + ((375 - 25) / 12) * ((hoursToMinutes(sunRiseTime) * 0.5) / 60); // 日出时间点
    const num1 = num3 - num4; // 两标记点之间的间距
    // 二阶贝塞尔曲线 绘制日出日落
    // 左下角虚线
    const sp1 = {
      x: 25,
      y: 75 + 15,
    };
    const cp1 = {
      x: num4 - (num4 - 25) / 2,
      y: 75 + 50 / 2,
    };
    const ep1 = {
      x: num4,
      y: 75,
    };
    // 上半部分实线
    const sp2 = {
      x: num4,
      y: 75,
    };
    const cp2 = {
      x: num4 + num1 / 2,
      y: num2,
    };
    const ep2 = {
      x: num3,
      y: 75,
    };
    // 右下角虚线
    const sp3 = {
      x: num3,
      y: 75,
    };
    const cp3 = {
      x: num3 + (375 - num3) / 2,
      y: 75 + 50 / 2,
    };
    const ep3 = {
      x: 375,
      y: 75 + 15,
    };

    const canvas: any = document.getElementById("besse-chart");
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    // 清空画布
    ctx.clearRect(0, 0, 400, 150);

    // 绘制中间虚线
    function drawAxisMiddleDashed() {
      ctx.setLineDash([]);
      ctx.beginPath();
      ctx.moveTo(25, 75); // 数轴起点
      ctx.lineTo(375, 75); // 数轴终点
      ctx.strokeStyle = "rgba(255,255,255,0.9)";
      ctx.lineWidth = 0.5;
      ctx.stroke();
      ctx.closePath();
    }
    // 绘制中间虚线
    drawAxisMiddleDashed();

    // 在数轴上取两个标记
    function markValue(x1: number, _x2: number) {
      // 打标记
      ctx.beginPath();
      ctx.arc(x1, 75, radius, 0, 2 * Math.PI);
      ctx.fillStyle = "transparent";
      ctx.fill();
      ctx.beginPath();
      ctx.arc(num3, 75, radius, 0, 2 * Math.PI);
      ctx.fillStyle = "transparent";
      ctx.fill();
    }
    // 打标记
    markValue(num4, num4 + num1);

    // 左下角虚线
    const drawAxisSunLeftBottomDashed = () => {
      ctx.beginPath();
      ctx.setLineDash([5, 5]);
      ctx.moveTo(sp1.x, sp1.y);
      ctx.quadraticCurveTo(cp1.x, cp1.y, ep1.x, ep1.y);
      ctx.strokeStyle = "#FFE400";
      ctx.lineWidth = 2;
      ctx.stroke();
      ctx.closePath();
    };
    drawAxisSunLeftBottomDashed();

    // 上半部分实线
    const drawAxisSunTopSolid = () => {
      ctx.beginPath();
      ctx.setLineDash([]);
      ctx.moveTo(sp2.x, sp2.y);
      ctx.quadraticCurveTo(cp2.x, cp2.y, ep2.x, ep2.y);
      ctx.strokeStyle = "#FFE400";
      ctx.lineWidth = 2;
      ctx.stroke();
      ctx.closePath();
    };
    drawAxisSunTopSolid();

    // 右下角虚线
    const drawAxisSunRightBottomDashed = () => {
      ctx.beginPath();
      ctx.setLineDash([5, 5]);
      ctx.moveTo(sp3.x, sp3.y);
      ctx.quadraticCurveTo(cp3.x, cp3.y, ep3.x, ep3.y);
      ctx.strokeStyle = "#FFE400";
      ctx.lineWidth = 2;
      ctx.stroke();
      ctx.closePath();
    };
    drawAxisSunRightBottomDashed();

    // 绘制最下面的数轴刻度线
    function drawAxisScale() {
      // 绘制刻度
      for (let i = 0; i < 12; i++) {
        const x = 25 + i * (350 / 12); // 刻度位置
        ctx.beginPath();
        // 绘制刻度值
        ctx.font = "12px Arial";
        ctx.fillStyle = "#fff";
        ctx.fillText(timeDataOfHours[i], x - 8, 150); // 刻度值文本位置
      }
    }
    drawAxisScale();

    // 图片标记
    // 参数：startPoint, controlPoint, endPoint
    // 当前日期时间 小于等于当天日出日期时间 此时应该显示月亮
    /* if (Date.parse(nowDateTime) <= Date.parse(sunRiseDateTime)) {
      const curvePath = getSecondPathPoint(sp1, cp1, ep1);
      const rate = hoursToMinutes(nowTime) / hoursToMinutes(sunRiseTime);
      const point = curvePath(rate);
      const image = new Image();
      image.src = moonUrl;
      image.onload = function () {
        const size = 15;
        ctx.beginPath();
        const imgX = point.x - size / 2;
        const imgY = point.y - size / 2;
        // 绘制图片
        ctx.drawImage(image, imgX, imgY, size, size); 
      };
    } */

    // 当前日期时间 大于当天日出日期时间 且小于等于当天日落日期时间 此时应该显示太阳
    if (
      sunRiseDateTime &&
      sunSetDateTime &&
      Date.parse(nowDateTime) > Date.parse(sunRiseDateTime) &&
      Date.parse(nowDateTime) <= Date.parse(sunSetDateTime)
    ) {
      const curvePath = getSecondPathPoint(sp2, cp2, ep2);
      const nowMinutes = hoursToMinutes(nowTime);
      const sunRiseMinutes = hoursToMinutes(sunRiseTime);
      const sunSetMinutes = hoursToMinutes(sunSetTime);
      const totalDuration = sunSetMinutes - sunRiseMinutes;
      let rate = 0;
      if (totalDuration > 0) {
        rate = (nowMinutes - sunRiseMinutes) / totalDuration;
      }
      rate = Math.max(0, Math.min(1, rate));

      const point = curvePath(rate);
      const image = new Image();
      image.src = sunUrl;
      image.onload = function () {
        const size = 15;
        ctx.beginPath();
        const imgX = point.x - size / 2;
        const imgY = point.y - size / 2;
        // 绘制图片
        ctx.drawImage(image, imgX, imgY, size, size);
      };
    }

    // 当前日期时间 大于当天日落日期时间 此时应该显示月亮
    /* if (Date.parse(nowDateTime) > Date.parse(sunSetDateTime)) {
      const curvePath = getSecondPathPoint(sp3, cp3, ep3);
      const rate = (hoursToMinutes(nowTime) - hoursToMinutes(sunSetTime)) / (hoursToMinutes("23:59:59") - hoursToMinutes(sunSetTime));
      const point = curvePath(rate);
      const image = new Image();
      image.src = moonUrl;
      image.onload = function () {
        const size = 15;
        ctx.beginPath();
        const imgX = point.x - size / 2;
        const imgY = point.y - size / 2;
        // 绘制图片
        ctx.drawImage(image, imgX, imgY, size, size); 
      };
    } */

    /* 三阶贝塞尔曲线 绘制月出月落 */
    const sp4 = {
      x: sp1.x,
      y: sp1.y + 10,
    };
    const cp4 = {
      x: num4 / 3,
      y: cp1.y / 3,
    };
    const cp5 = {
      x: cp2.x - 50,
      y: 175,
    };
    const ep4 = {
      x: ep3.x,
      y: ep3.y - 10,
    };
    // 下方虚线
    const drawAxisMoonBottomDashed = () => {
      ctx.strokeStyle = "#0269bd";
      ctx.setLineDash([5, 5]);
      ctx.beginPath();
      ctx.moveTo(sp4.x, sp4.y);
      ctx.bezierCurveTo(cp4.x, cp4.y, cp5.x, cp5.y, ep4.x, ep4.y);
      ctx.stroke();
      ctx.closePath();
    };
    drawAxisMoonBottomDashed();

    // 当前时间 大于当天的月出时间 且小于等于第二天的月落时间 此时应该显示月亮
    if (
      moonRiseDateTime &&
      moonSetDateTime &&
      Date.parse(nowDateTime) > Date.parse(moonRiseDateTime) &&
      Date.parse(nowDateTime) <= Date.parse(moonSetDateTime)
    ) {
      const bezierPath = getThirdPathPoint(sp4, cp4, cp5, ep4);
      const moonRiseMinutes = hoursToMinutes(moonRiseTime);
      const nowMinutes = hoursToMinutes(nowTime);
      const rate = Math.max(0, Math.min(1, nowMinutes / moonRiseMinutes));
      const point = bezierPath(rate);
      const image = new Image();
      image.src = moonUrl;
      image.onload = function () {
        const size = 15;
        ctx.beginPath();
        const imgX = point.x - size / 2;
        const imgY = point.y - size / 2;
        // 绘制图片
        ctx.drawImage(image, imgX, imgY, size, size);
      };
    }
  }, [SunMoon, dayjs]);

  return (
    <canvas
      id="besse-chart"
      width="400"
      height="150"
      style={{
        height: pxChangeVw("100px"),
        width: "100%",
      }}
    ></canvas>
  );
};

// 可缩放版本贝塞尔曲线图表组件（Pad版）
const BesselChartPad: React.FC<any> = ({ SunMoon, scale = 2 }: any) => {
  const { dayjs } = useDayjs();

  // 基于外部传入的 scale 计算所有尺寸
  const CANVAS_WIDTH = 400 * scale;
  const CANVAS_HEIGHT = 150 * scale;
  const AXIS_START = 25 * scale;
  const AXIS_MIDDLE_Y = 75 * scale;
  const AXIS_END = 375 * scale;

  useEffect(() => {
    if (!SunMoon || !SunMoon.Sun || !SunMoon.Moon) return;

    const sunRiseTime = SunMoon?.Sun?.Rise;
    const nowTime = dayjs().format("HH:mm:ss");
    const nowDateTime = dayjs().format("YYYY-MM-DD HH:mm:ss");
    const sunSetTime = SunMoon?.Sun?.Set;
    const sunRiseDateTime = SunMoon?.Sun?.EpochRise;
    const sunSetDateTime = SunMoon?.Sun?.EpochSet;

    const moonRiseTime = SunMoon?.Moon?.Rise;
    const moonRiseDateTime = SunMoon?.Moon?.EpochRise;
    const moonSetDateTime = SunMoon?.Moon?.EpochSet;

    const timeDataOfHours = getHoursArray();
    const num2 = -35 * scale;
    const radius = 2 * scale;

    const sunSetMinutes = hoursToMinutes(sunSetTime);
    const sunRiseMinutes = hoursToMinutes(sunRiseTime);
    const num3 = AXIS_START + ((AXIS_END - AXIS_START) / 12) * ((sunSetMinutes * 0.5) / 60);
    const num4 = AXIS_START + ((AXIS_END - AXIS_START) / 12) * ((sunRiseMinutes * 0.5) / 60);
    const num1 = num3 - num4;

    const sp1 = { x: AXIS_START, y: AXIS_MIDDLE_Y + 15 * scale };
    const cp1 = { x: num4 - (num4 - AXIS_START) / 2, y: AXIS_MIDDLE_Y + (50 * scale) / 2 };
    const ep1 = { x: num4, y: AXIS_MIDDLE_Y };

    const sp2 = { x: num4, y: AXIS_MIDDLE_Y };
    const cp2 = { x: num4 + num1 / 2, y: num2 };
    const ep2 = { x: num3, y: AXIS_MIDDLE_Y };

    const sp3 = { x: num3, y: AXIS_MIDDLE_Y };
    const cp3 = { x: num3 + (AXIS_END - num3) / 2, y: AXIS_MIDDLE_Y + (50 * scale) / 2 };
    const ep3 = { x: AXIS_END, y: AXIS_MIDDLE_Y + 15 * scale };

    const sp4 = { x: sp1.x, y: sp1.y + 10 * scale };
    const cp4 = { x: num4 / 3, y: cp1.y / 3 };
    const cp5 = { x: cp2.x - 50 * scale, y: 175 * scale };
    const ep4 = { x: ep3.x, y: ep3.y - 10 * scale };

    const canvas: any = document.getElementById(`besse-chart-pad-${scale}`); // 避免多个组件id冲突
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    ctx.clearRect(0, 0, CANVAS_WIDTH, CANVAS_HEIGHT);

    function drawAxisMiddleDashed() {
      ctx.setLineDash([]);
      ctx.beginPath();
      ctx.moveTo(AXIS_START, AXIS_MIDDLE_Y);
      ctx.lineTo(AXIS_END, AXIS_MIDDLE_Y);
      ctx.strokeStyle = "rgba(255,255,255,0.9)";
      ctx.lineWidth = 0.5 * scale;
      ctx.stroke();
      ctx.closePath();
    }
    drawAxisMiddleDashed();

    function markValue(x1: number) {
      ctx.beginPath();
      ctx.arc(x1, AXIS_MIDDLE_Y, radius, 0, 2 * Math.PI);
      ctx.fillStyle = "transparent";
      ctx.fill();
      ctx.beginPath();
      ctx.arc(num3, AXIS_MIDDLE_Y, radius, 0, 2 * Math.PI);
      ctx.fillStyle = "transparent";
      ctx.fill();
    }
    markValue(num4);

    const drawAxisSunLeftBottomDashed = () => {
      ctx.beginPath();
      ctx.setLineDash([5 * scale, 5 * scale]);
      ctx.moveTo(sp1.x, sp1.y);
      ctx.quadraticCurveTo(cp1.x, cp1.y, ep1.x, ep1.y);
      ctx.strokeStyle = "#FFE400";
      ctx.lineWidth = 2 * scale;
      ctx.stroke();
      ctx.closePath();
    };
    drawAxisSunLeftBottomDashed();

    const drawAxisSunTopSolid = () => {
      ctx.beginPath();
      ctx.setLineDash([]);
      ctx.moveTo(sp2.x, sp2.y);
      ctx.quadraticCurveTo(cp2.x, cp2.y, ep2.x, ep2.y);
      ctx.strokeStyle = "#FFE400";
      ctx.lineWidth = 2 * scale;
      ctx.stroke();
      ctx.closePath();
    };
    drawAxisSunTopSolid();

    const drawAxisSunRightBottomDashed = () => {
      ctx.beginPath();
      ctx.setLineDash([5 * scale, 5 * scale]);
      ctx.moveTo(sp3.x, sp3.y);
      ctx.quadraticCurveTo(cp3.x, cp3.y, ep3.x, ep3.y);
      ctx.strokeStyle = "#FFE400";
      ctx.lineWidth = 2 * scale;
      ctx.stroke();
      ctx.closePath();
    };
    drawAxisSunRightBottomDashed();

    function drawAxisScale() {
      for (let i = 0; i < 12; i++) {
        const x = AXIS_START + i * ((AXIS_END - AXIS_START) / 12);
        ctx.font = `${12 * scale}px Arial`;
        ctx.fillStyle = "#fff";
        ctx.fillText(timeDataOfHours[i], x - 8 * scale, CANVAS_HEIGHT);
      }
    }
    drawAxisScale();

    if (
      sunRiseDateTime &&
      sunSetDateTime &&
      Date.parse(nowDateTime) > Date.parse(sunRiseDateTime) &&
      Date.parse(nowDateTime) <= Date.parse(sunSetDateTime)
    ) {
      const curvePath = getSecondPathPoint(sp2, cp2, ep2);
      const nowMinutes = hoursToMinutes(nowTime);
      const totalDuration = sunSetMinutes - sunRiseMinutes;
      let rate = 0;
      if (totalDuration > 0) {
        rate = (nowMinutes - sunRiseMinutes) / totalDuration;
      }
      rate = Math.max(0, Math.min(1, rate));

      const point = curvePath(rate);
      const image = new Image();
      image.src = sunUrl;
      image.onload = function () {
        const size = 15 * scale;
        const imgX = point.x - size / 2;
        const imgY = point.y - size / 2;
        ctx.drawImage(image, imgX, imgY, size, size);
      };
    }

    const drawAxisMoonBottomDashed = () => {
      ctx.strokeStyle = "#0269bd";
      ctx.setLineDash([5 * scale, 5 * scale]);
      ctx.beginPath();
      ctx.moveTo(sp4.x, sp4.y);
      ctx.bezierCurveTo(cp4.x, cp4.y, cp5.x, cp5.y, ep4.x, ep4.y);
      ctx.lineWidth = 2 * scale;
      ctx.stroke();
      ctx.closePath();
    };
    drawAxisMoonBottomDashed();

    if (
      moonRiseDateTime &&
      moonSetDateTime &&
      Date.parse(nowDateTime) > Date.parse(moonRiseDateTime) &&
      Date.parse(nowDateTime) <= Date.parse(moonSetDateTime)
    ) {
      const bezierPath = getThirdPathPoint(sp4, cp4, cp5, ep4);
      const moonRiseMinutes = hoursToMinutes(moonRiseTime);
      const nowMinutes = hoursToMinutes(nowTime);
      const rate = Math.max(0, Math.min(1, nowMinutes / moonRiseMinutes));
      const point = bezierPath(rate);
      const image = new Image();
      image.src = moonUrl;
      image.onload = function () {
        const size = 15 * scale;
        const imgX = point.x - size / 2;
        const imgY = point.y - size / 2;
        ctx.drawImage(image, imgX, imgY, size, size);
      };
    }
  }, [SunMoon, dayjs, scale]); // 依赖添加 scale，变化时重绘

  return (
    <div
      style={{
        width: "95%",
        maxWidth: "834px",
        margin: "0 auto",
      }}
    >
      {/* id 加入 scale 避免多个组件冲突 */}
      <canvas
        id={`besse-chart-pad-${scale}`}
        width={CANVAS_WIDTH}
        height={CANVAS_HEIGHT}
        style={{
          height: pxChangeVw(`${100 * scale}px`), // 显示高度随 scale 变化
          width: "100%",
          display: "block",
        }}
      ></canvas>
    </div>
  );
};

// 导出两个组件，可根据需要选择使用
export { BesselChart, BesselChartPad };
// 默认导出基础版本
export default BesselChart;
